import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  average,
  formatHours,
  formatRating,
  summarizeWellness,
  HISTORY_DAYS,
  INSIGHT_DAYS,
  RANGE_OPTIONS,
  type RangeKey,
} from "@consistency-circle/shared";

import { CalendarHeatmap } from "../../components/CalendarHeatmap";
import { apiClient } from "../../lib/api";
import { addDays, periodOverPeriodDelta, rollingConsistency, todayString } from "../../lib/goal-stats";
import { pickWeeklyInsight } from "../../lib/insights";

import { CollapsibleSection } from "./components/CollapsibleSection";
import { DayCard } from "./components/DayCard";
import { GoalBreakdownCard } from "./components/GoalBreakdownCard";
import { HeadlineCard } from "./components/HeadlineCard";
import { SectionHeader } from "./components/SectionHeader";
import { StatCard } from "./components/StatCard";
import { TrendChart } from "./components/TrendChart";
import { WellnessRelationshipCard } from "./components/WellnessRelationshipCard";
import { WELLNESS_METRICS } from "./constants";

export function ReviewScreen() {
  const [range, setRange] = useState<RangeKey>("week");
  const rangeDays = RANGE_OPTIONS.find((option) => option.key === range)?.days ?? 7;
  const to = todayString();
  const from = addDays(to, -(rangeDays - 1));
  const historyFrom = addDays(to, -(HISTORY_DAYS - 1));

  // One 12-week fetch each for goal history and daily entries; every section
  // slices the selected range (or its own labeled window) out of these.
  const {
    data: history,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["goal-history", historyFrom, to],
    queryFn: () => apiClient.getDailyGoalHistory(historyFrom, to),
  });

  const {
    data: allEntries,
    isPending: entriesPending,
    isError: entriesError,
  } = useQuery({
    queryKey: ["daily-entries", historyFrom, to],
    queryFn: () => apiClient.getDailyEntries(historyFrom, to),
  });

  const days84 = history?.days ?? [];
  const days = days84.filter((day) => day.localDate >= from);
  const goalsInRange = (history?.goals ?? [])
    .map((goal) => ({ ...goal, days: goal.days.filter((day) => day.localDate >= from) }))
    .filter((goal) => goal.days.length > 0);

  const entries = (allEntries ?? []).filter((entry) => entry.localDate >= from);
  const insightEntries = (allEntries ?? []).filter(
    (entry) => entry.localDate >= addDays(to, -(INSIGHT_DAYS - 1)),
  );

  const trackedDays = days.filter((day) => day.totalCount > 0);
  const consistency = rollingConsistency(days, rangeDays);
  const delta = periodOverPeriodDelta(days84, rangeDays);
  const avgMood = average(entries.map((entry) => entry.mood));
  const wellness = summarizeWellness(entries);
  const insight = pickWeeklyInsight(insightEntries);
  const moodByDate = new Map(
    (allEntries ?? [])
      .filter((entry) => entry.mood !== null)
      .map((entry) => [entry.localDate, entry.mood as number]),
  );

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Your progress
          </p>
          <h1 className="text-2xl font-semibold tracking-tight">Review</h1>
        </div>

        <div className="flex rounded-full border border-border bg-muted/40 p-0.5">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              onClick={() => setRange(option.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                range === option.key
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      {(isPending || entriesPending) && <p className="text-sm text-muted-foreground">Loading...</p>}
      {(isError || entriesError) && (
        <p className="text-sm text-destructive">Failed to load review.</p>
      )}

      {history && allEntries && (
        <>
          <HeadlineCard
            rangeLabel={RANGE_OPTIONS.find((option) => option.key === range)?.label ?? ""}
            consistency={consistency}
            avgMood={avgMood}
            delta={delta}
            insight={insight}
          />

          {range === "quarter" ? (
            <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Last 12 weeks
              </p>
              <CalendarHeatmap days={days84} todayDate={to} />
            </div>
          ) : (
            <TrendChart days={days} moodByDate={moodByDate} />
          )}

          {trackedDays.length === 0 && entries.length === 0 && (
            <div className="space-y-1.5 rounded-2xl border border-dashed border-border p-10 text-center">
              <p className="text-sm font-medium">Nothing tracked yet</p>
              <p className="text-sm text-muted-foreground">
                Once you add goals and check-ins, your progress will gather here one gentle day at
                a time.
              </p>
            </div>
          )}

          <section className="space-y-4">
            <SectionHeader
              eyebrow="Wellness"
              title="What lines up with better days"
              description="How sleep, diet, and exercise compare between your higher- and lower-feeling days."
            />

            {wellness.trackedMoodDays === 0 ? (
              <div className="space-y-1.5 rounded-2xl border border-dashed border-border p-8 text-center">
                <p className="text-sm font-medium">No wellness check-ins yet</p>
                <p className="text-sm text-muted-foreground">
                  Add a few daily check-ins from the dashboard and patterns will show up here.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {WELLNESS_METRICS.map((metric) => (
                    <StatCard
                      key={metric.key}
                      icon={<metric.Icon className="h-4 w-4" />}
                      label={`Avg ${metric.label.toLowerCase()}`}
                      value={(metric.kind === "hours" ? formatHours : formatRating)(
                        wellness.averages[metric.key],
                      )}
                    />
                  ))}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {WELLNESS_METRICS.map((metric) => (
                    <WellnessRelationshipCard
                      key={metric.key}
                      metric={metric}
                      relationship={wellness.relationships[metric.key]}
                    />
                  ))}
                </div>
              </>
            )}
          </section>

          {goalsInRange.length > 0 && (
            <CollapsibleSection title="By goal" summary={`${goalsInRange.length} goals`}>
              <div className="space-y-2.5">
                {goalsInRange.map((goal) => (
                  <GoalBreakdownCard key={goal.goalId} goal={goal} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {range !== "quarter" && trackedDays.length > 0 && (
            <CollapsibleSection title="Day by day" summary={`${days.length} days`}>
              <div className="space-y-2.5">
                {[...days].reverse().map((day) => (
                  <DayCard key={day.localDate} day={day} />
                ))}
              </div>
            </CollapsibleSection>
          )}
        </>
      )}
    </div>
  );
}
